import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import Loader from "./loader";
import Wrapper from "../wrapper";
import { IoLogoWhatsapp, IoCheckmarkCircle } from "react-icons/io5";
import { submitRegistration, subscribeToSiteSettings } from "../../lib/firestoreContent";

function Submit() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState("https://chat.whatsapp.com/GbToN7d2xVN7RpDIXMrOLC");

  const [generalData, setGeneralData] = useState({});
  const [courseData, setCourseData] = useState({});

  useEffect(() => {
    try {
      const g = JSON.parse(localStorage.getItem("formData")) || {};
      const c = JSON.parse(localStorage.getItem("courseFormData")) || {};
      setGeneralData(g);
      setCourseData(c);
    } catch (e) {
      console.error("Error reading saved application data:", e);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToSiteSettings((settings) => {
      if (settings?.whatsappCommunityUrl) {
        setWhatsappUrl(settings.whatsappCommunityUrl);
      }
    });
    return () => unsubscribe && unsubscribe();
  }, []);

  const hasRequiredFields =
    generalData.firstName &&
    generalData.lastName &&
    generalData.email &&
    generalData.phone &&
    courseData.preferredLearningTrack;

  const handleSubmit = async () => {
    if (!hasRequiredFields) {
      setErrorMessage("Please complete all required fields in the previous steps before submitting.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    const mergedFormData = { ...generalData, ...courseData };

    try {
      await submitRegistration(mergedFormData);
      localStorage.removeItem("formData");
      localStorage.removeItem("courseFormData");
      setSuccess(true);
    } catch (error) {
      console.error("Registration error:", error);
      setErrorMessage(error.message || "An error occurred while submitting your registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Clash Display', sans-serif",
      }}
      className="pb-16"
    >
      <Wrapper>
        {loading && <Loader />}

        {/* Step Indicator Navigation */}
        <nav className="pt-32 md:pt-40">
          <ul
            style={{
              fontFamily: "'Clash Display', sans-serif",
              fontWeight: 500,
            }}
            className="flex w-[90%] md:w-[70%] m-auto justify-between text-[13px] md:text-sm border-b border-slate-200 pb-3"
          >
            <li>
              <NavLink
                to="/register"
                className={({ isActive }) =>
                  `transition ${isActive ? "font-bold text-primary underline underline-offset-4" : "text-slate-500 hover:text-primary"}`
                }
              >
                1. General Details
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/course-selection"
                className={({ isActive }) =>
                  `transition ${isActive ? "font-bold text-primary underline underline-offset-4" : "text-slate-500 hover:text-primary"}`
                }
              >
                2. Course Selection
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/submit"
                className={({ isActive }) =>
                  `transition ${isActive ? "font-bold text-primary underline underline-offset-4" : "text-slate-500 hover:text-primary"}`
                }
              >
                3. Review & Submit
              </NavLink>
            </li>
          </ul>
        </nav>

        {success ? (
          /* ================= SUCCESS CONFIRMATION STATE ================= */
          <div className="w-[90%] md:w-[65%] mx-auto mt-10 bg-white rounded-2xl shadow-xl border border-slate-100 p-8 md:p-12 text-center animate-fadeIn">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-6 shadow-inner">
              <IoCheckmarkCircle size={56} />
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-3">
              Application Submitted!
            </h2>
            <p
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-slate-600 text-sm md:text-base max-w-lg mx-auto mb-8 leading-relaxed"
            >
              Thank you for registering with XploreSTEMi. Your application has been recorded. Join our official community group to receive orientation notices and onboarding details.
            </p>

            {/* WhatsApp Community Invite Card */}
            <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-6 max-w-md mx-auto mb-8 text-left flex items-start gap-4">
              <div className="p-3 bg-emerald-500 text-white rounded-xl shadow-md shrink-0">
                <IoLogoWhatsapp size={32} />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-base mb-1">
                  Connect on WhatsApp
                </h4>
                <p style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-xs text-slate-600 mb-3">
                  Stay updated on cohort schedules, mentors, and class links.
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 transition"
                >
                  Join Community Group →
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/"
                className="w-full sm:w-auto rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-[#1b1449]"
              >
                Return to Homepage
              </Link>
              <Link
                to="/training"
                className="w-full sm:w-auto rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Browse Our Programs
              </Link>
            </div>
          </div>
        ) : (
          /* ================= REVIEW & SUBMIT APPLICATION STATE ================= */
          <div className="w-[90%] md:w-[70%] mx-auto mt-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-dark mb-2">
                Review Your Application
              </h1>
              <p
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-slate-500 text-sm"
              >
                Please verify that your personal details and selected course are accurate before submitting.
              </p>
            </div>

            {!hasRequiredFields && (
              <div className="mb-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-amber-800 text-sm flex items-center justify-between">
                <span>Some required fields are missing. Please complete them before submitting.</span>
                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className="text-xs font-bold underline ml-3"
                >
                  Go to Step 1
                </button>
              </div>
            )}

            {/* Review Cards Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Card 1: Personal Details */}
              <div className="rounded-xl bg-white p-6 shadow-md border border-slate-100 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
                    <h3 className="font-bold text-dark text-base">Personal Information</h3>
                    <Link
                      to="/register"
                      className="text-xs font-semibold text-primary hover:underline"
                    >
                      Edit ✏️
                    </Link>
                  </div>
                  <dl
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="space-y-2 text-xs md:text-sm text-slate-700"
                  >
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Full Name:</dt>
                      <dd className="font-semibold text-slate-900">
                        {generalData.firstName ? `${generalData.firstName} ${generalData.lastName}` : "—"}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Email:</dt>
                      <dd className="font-medium">{generalData.email || "—"}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Phone:</dt>
                      <dd className="font-medium">{generalData.phone || "—"}</dd>
                    </div>
                    {generalData.whatsappNumber && (
                      <div className="flex justify-between">
                        <dt className="text-slate-400">WhatsApp:</dt>
                        <dd className="font-medium">{generalData.whatsappNumber}</dd>
                      </div>
                    )}
                    {generalData.gender && (
                      <div className="flex justify-between">
                        <dt className="text-slate-400">Gender:</dt>
                        <dd className="font-medium">{generalData.gender}</dd>
                      </div>
                    )}
                    {generalData.age && (
                      <div className="flex justify-between">
                        <dt className="text-slate-400">Age:</dt>
                        <dd className="font-medium">{generalData.age}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              {/* Card 2: Course & Learning Track */}
              <div className="rounded-xl bg-white p-6 shadow-md border border-slate-100 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
                    <h3 className="font-bold text-dark text-base">Course & Track</h3>
                    <Link
                      to="/course-selection"
                      className="text-xs font-semibold text-primary hover:underline"
                    >
                      Edit ✏️
                    </Link>
                  </div>
                  <dl
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="space-y-2 text-xs md:text-sm text-slate-700"
                  >
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Selected Track:</dt>
                      <dd className="font-semibold text-primary">
                        {courseData.preferredLearningTrack || "—"}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Experience:</dt>
                      <dd className="font-medium">{courseData.experienceLevel || "—"}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-400">Availability:</dt>
                      <dd className="font-medium">{courseData.availability || "—"}</dd>
                    </div>
                    {courseData.motivationForJoining && (
                      <div className="pt-2">
                        <dt className="text-slate-400 mb-1">Motivation:</dt>
                        <dd className="text-xs text-slate-600 line-clamp-3 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                          {courseData.motivationForJoining}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </div>

            {errorMessage && (
              <p role="alert" className="mb-6 rounded-xl bg-red-50 p-4 text-center text-sm text-red-700 border border-red-200">
                {errorMessage}
              </p>
            )}

            {/* Bottom Actions */}
            <div className="flex items-center justify-between">
              <NavLink
                to="/course-selection"
                className="rounded-xl border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                ← Back
              </NavLink>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !hasRequiredFields}
                className="rounded-xl bg-primary px-8 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-[#1b1449] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Submitting Application…" : "Confirm & Submit Application"}
              </button>
            </div>
          </div>
        )}
      </Wrapper>
    </div>
  );
}

export default Submit;
